import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, X } from 'lucide-react';

/**
 * In-app banner for pushes that arrive while the app is OPEN (UI tweak #7).
 * Android suppresses the OS banner when the app is foregrounded, so
 * native/push.js re-emits 'pushNotificationReceived' as a
 * 'marakin:push-foreground' window event and this component renders it.
 *
 * - Slides in under the navbar, auto-dismisses after 6s.
 * - Tapping it routes to the same data.url deep link an OS tap would
 *   (e.g. "/backwell-rfc?event=123" opens the expanded event card).
 * - Marakin brand: green panel, gold accent, cream text.
 */
const AUTO_DISMISS_MS = 6000;

const PushBanner = () => {
  const [toast, setToast] = useState(null); // { title, body, url }
  const [leaving, setLeaving] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const timerRef = useRef(null);
  const leaveRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setLeaving(true);
    leaveRef.current = setTimeout(() => { setToast(null); setLeaving(false); }, 220);
  }, []);

  useEffect(() => {
    const onPush = (e) => {
      clearTimeout(timerRef.current);
      clearTimeout(leaveRef.current);
      setLeaving(false);
      setToast(e.detail);
      timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
      // A push almost always means new/changed content — refresh the feeds in
      // the background so new event cards appear (collapsed) without the user
      // having to leave and re-enter the section. Prefix match covers
      // ['events', clubSlug, ...] and ['announcements', clubSlug, ...].
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['announcements'] });
    };
    window.addEventListener('marakin:push-foreground', onPush);
    return () => {
      window.removeEventListener('marakin:push-foreground', onPush);
      clearTimeout(timerRef.current);
      clearTimeout(leaveRef.current);
    };
  }, [dismiss, qc]);

  if (!toast) return null;

  const open = () => {
    const url = toast.url;
    dismiss();
    if (url && typeof url === 'string' && url.startsWith('/')) {
      // navigate() keeps SPA state; ?event=123 deep links re-parse on the hub.
      navigate(url);
    }
  };

  return (
    <div
      role="alert"
      onClick={open}
      style={{
        position: 'fixed',
        top: 'calc(var(--sat) + 64px)', /* B-15 */
        left: 12,
        right: 12,
        zIndex: 1300,
        maxWidth: 560,
        margin: '0 auto',
        background: 'var(--green, #1E4A35)',
        color: 'var(--cream, #F4EFE6)',
        border: '1px solid rgba(196,148,58,0.55)',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        cursor: 'pointer',
        animation: leaving ? 'marakinToastOut 0.22s ease forwards' : 'marakinToastIn 0.25s ease',
      }}
    >
      <style>{`
        @keyframes marakinToastIn  { from { transform: translateY(-16px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes marakinToastOut { from { transform: none; opacity: 1; } to { transform: translateY(-16px); opacity: 0; } }
      `}</style>
      <span style={{
        flexShrink: 0, width: 34, height: 34, borderRadius: 9,
        background: 'rgba(196,148,58,0.22)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Bell size={17} color="#C4943A" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontFamily: "'Nunito', sans-serif", fontWeight: 800,
          fontSize: 14, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {toast.title}
        </span>
        {toast.body && (
          <span style={{
            fontSize: 13, marginTop: 2, color: 'rgba(244,239,230,0.85)',
            lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {toast.body}
          </span>
        )}
        {toast.url && (
          <span style={{ display: 'block', fontSize: 12, marginTop: 4, color: '#C4943A', fontWeight: 700 }}>
            Tap to view
          </span>
        )}
      </span>
      <button
        aria-label="Dismiss notification"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        style={{
          flexShrink: 0, background: 'transparent', border: 'none',
          color: 'rgba(244,239,230,0.6)', padding: 4, cursor: 'pointer',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default PushBanner;
